/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-nocheck

import { gsap } from 'gsap';
import { Object3D } from 'three';
import { Text } from 'troika-three-text';

import type CardGL from '../../CardFGUp';

import PerformanceProfile from '../../../../utils/PerformanceProfile';

const GLOBAL_CONFIG = {
    letterSpacing: -0.04,
    fontSizeTitle: 0.15,
    // fontSizeCollection: 0.04,
    fontSizeCollection: 0.07,
    margin: 0.01,
    color: '#ffe3e3',
};

const CONFIG = [
    {
        // Tier0
        color: '#ffffff',
    },
    {
        // Tier1
        color: '#789eff',
    },
    {
        // Tier2

        color: '#b578ff',
    },
    {
        // Tier3
        color: '#ffc701',
    },
];

let GUI;

const addGUI = async () => {
    await PerformanceProfile.devModeReady();

    GUI = PerformanceProfile.GUI.floorGameUp.addFolder({
        title: 'EndText',
        expanded: false,
    });

    GUI.addButton({ title: 'Export Config' }).on('click', () => {
        PerformanceProfile.exportConfig('EndText', { ...GLOBAL_CONFIG, ...CONFIG });
    });

    GUI.addInput(GLOBAL_CONFIG, 'letterSpacing', { min: -0.1, max: 0, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'fontSizeTitle', { min: 0.01, max: 0.4, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'fontSizeCollection', { min: 0.01, max: 0.15, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'margin', { min: 0, max: 0.3, step: 0.001 });
    GUI.addInput(GLOBAL_CONFIG, 'color');

    for (let i = 0; i < CONFIG.length; i++) {
        const folder = GUI.addFolder({
            title: `Tier ${i + 1}`,
            expanded: false,
        });

        folder.addInput(CONFIG[i], 'color');
    }
};

if (PerformanceProfile.devMode) addGUI();

interface EndTextOptions {
    tier: number;
    floorPrice: number;
    collectionName: string;
    isCollectionView?: boolean;
}

export default class EndText extends Object3D {
    tier: number;
    content: string[];
    isCollectionView: boolean;

    time = 0.0;
    visible = false;

    text: Text;
    texts: Text[];

    // TODO: These need to be explicitly set within the constructor.
    // Otherwise, it needs to accept `undefined`.
    textMargin: number;

    // TODO: None of these properties are used in this class.
    textSize: number;
    textColor: string;
    card: CardGL;

    constructor({ tier, floorPrice, collectionName, isCollectionView }: EndTextOptions) {
        super();

        this.tier = tier;
        this.content = [`New ${floorPrice} Eth Floor!`, collectionName];
        this.isCollectionView = Boolean(isCollectionView);

        this.texts = [];
        this.initText();
    }

    initText() {
        this.textMargin = GLOBAL_CONFIG.fontSizeTitle + GLOBAL_CONFIG.margin;

        for (let i = 0; i < this.content.length; i++) {
            const text = new Text();
            this.add(text);

            text.font = '/assets/fonts/tusker-grotesk-4500-medium-webgl.woff';
            text.text = this.content[i].toUpperCase();
            text.fontSize = i === 0 ? GLOBAL_CONFIG.fontSizeTitle : GLOBAL_CONFIG.fontSizeCollection;

            text.textAlign = 'center';
            text.anchorX = 'center';
            text.anchorY = 'top';
            text.letterSpacing = GLOBAL_CONFIG.letterSpacing;

            text.color = CONFIG[this.tier].color;
            text.position.y = this.textMargin * -i;
            text.__saveY = text.position.y;

            text.sync();
            this.texts.push(text);
        }

        if (PerformanceProfile.devMode) {
            GUI.on('change', this.updateTextConfig.bind(this));
        }
    }

    updateTextConfig() {
        this.textMargin = GLOBAL_CONFIG.fontSizeTitle + GLOBAL_CONFIG.margin;

        this.texts.forEach((text, i) => {
            text.fontSize = i === 0 ? GLOBAL_CONFIG.fontSizeTitle : GLOBAL_CONFIG.fontSizeCollection;
            text.letterSpacing = GLOBAL_CONFIG.letterSpacing;
            text.color = CONFIG[this.tier].color;
            text.position.y = this.textMargin * -i;
            text.__saveY = text.position.y;
            text.sync();
        });

        this.visible = true;
    }

    animateIn(delay: number) {
        this.visible = true;

        this.texts.forEach((text, index) => {
            gsap.fromTo(
                text.material,
                {
                    opacity: 0,
                },
                {
                    opacity: 1,
                    ease: 'power2.out',
                    duration: 1.2,
                    delay: delay + index * 0.025,
                },
            );

            gsap.fromTo(
                text.position,
                {
                    y: text.__saveY - 0.15 - 0.02 * index,
                },
                {
                    y: text.__saveY,
                    ease: 'power3.out',
                    duration: 1.0,
                    delay: delay + index * 0.025,
                },
            );
        });
    }

    animateOut(delay: number) {
        gsap.fromTo(
            this.position,
            {
                y: 0.32,
            },
            {
                y: 1.3,
                ease: 'power1.inOut',
                duration: 1.2,
                delay,
                onComplete: () => {
                    this.visible = false;
                },
            },
        );
    }

    setPosition(width: number, _height: number) {
        if (this.isCollectionView) {
            this.position.x = 0.28;
            this.position.y = 0.1;
            this.position.z = 2.8;
        } else {
            if (width * 0.5 > 1100) {
                this.position.x = 0.6;
                this.position.y = 0.2;
                this.position.z = 1;
            } else {
                this.position.x = 0;
                this.position.y = -0.5;
                this.position.z = 1.5;
            }
        }
    }

    // update(elapsed) {}

    delete() {
        this.texts.forEach((text) => {
            this.remove(text);
            text.dispose();
        });
    }
}
